<template>
  <v-dialog v-model="show" persistent max-width="624px">
    <v-card class="dialog-container">
      <div class="dialog-header">
        <div>
          <p class="dialog-title">{{ adminText('workflowsAll.update_gratis_ticket_8ea1c70', 'Update Gratis Ticket') }}</p>
          <p class="dialog-subtitle">
            {{ adminText('workflowsAll.review_and_update_the_ticket_information_belo_a8da17a', 'Review and update the ticket information below to ensure all details are accurate and reflect the latest status.') }}
          </p>
        </div>
        <v-icon class="close-icon" @click="closeDialog">close</v-icon>
      </div>
      <entry-date-range-fields :form="form" :validator="$v.form" :event="event" />
      <div class="dialog-actions">
        <main-button :text="adminText('workflowsAll.cancel_77dfd21', 'Cancel')" flat outline @click.prevent="closeDialog" />
        <main-button :text="adminText('workflowsAll.update_fb91e24', 'Update')" :loading="saving" @click.prevent="submit" />
      </div>
      <simple-snackbar :open.sync="snackbarOpen" :message-prop="workflowLabel(snackbarMessage)" color="red" />
    </v-card>
  </v-dialog>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { requiredIf } from 'vuelidate/lib/validators'
import EntryDateRangeFields from './entry-date-range-fields.vue'
import MainButton from '../shared/main-button'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'
import { submitGuard } from '../../utils'
import { ValidityType } from '@/types'
import { UPDATE_GRATIS_ORDER_ITEM } from '@/graphql/orderItem.graphql'

export default {
  mixins: [WorkflowDisplay],
  name: 'update-gratis-ticket-modal',
  components: { EntryDateRangeFields, MainButton, SimpleSnackbar },
  props: {
    show: Boolean,
    ticket: {
      type: Object,
      required: true,
    },
    eventId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: {
        hasDateValidationRange: !!this.ticket.hasDateValidationRange,
        entryFrom: this.ticket.entryFrom || null,
        entryTo: this.ticket.entryTo || null,
        validityType: this.ticket.validityType || null,
      },
      saving: false,
      snackbarOpen: false,
      snackbarMessage: '',
    }
  },
  computed: {
    event() {
      return this.ticket.event || {}
    },
  },
  validations() {
    return {
      form: {
        hasDateValidationRange: {},
        entryFrom: { required: requiredIf(() => !!this.form.hasDateValidationRange) },
        entryTo: {
          required: requiredIf(
            () =>
              !!this.form.hasDateValidationRange &&
              this.form.validityType === ValidityType.MULTIPLE_DAYS,
          ),
        },
      },
    }
  },
  methods: {
    closeDialog() {
      this.$emit('update:show', false)
    },
    getUpdateErrorMessage(error) {
      const networkError = error && error.networkError
      const errors = [
        ...((error && error.graphQLErrors) || []),
        ...((networkError && networkError.result && networkError.result.errors) || []),
      ]
      const messages = errors.map(item => item && item.message)
      messages.push(networkError && networkError.message, error && error.message)

      return messages.find(message => typeof message === 'string' && message.trim() &&
        !/^(?:(?:Network|GraphQL) error:\s*)?(?:undefined|null)?$/i.test(message.trim())) ||
        'workflowsAll.something_went_wrong_please_try_again_9a3ea05'
    },
    async submit() {
      await submitGuard(this, async () => {
        this.saving = true
        this.snackbarOpen = false
        const { hasDateValidationRange, validityType } = this.form
        const input = {
          hasDateValidationRange,
          entryFrom: hasDateValidationRange ? this.form.entryFrom : null,
          entryTo: hasDateValidationRange ? this.form.entryTo : null,
          validityType: hasDateValidationRange ? validityType : null,
        }
        try {
          const { data, errors } = await this.$apollo.mutate({
            mutation: UPDATE_GRATIS_ORDER_ITEM,
            errorPolicy: 'none',
            variables: {
              orderItemId: this.ticket.id,
              eventId: this.eventId,
              input,
            },
          })
          if (errors && errors.length) throw new Error(this.getUpdateErrorMessage({ graphQLErrors: errors }))
          if (!data || !data.updateGratisOrderItem || String(data.updateGratisOrderItem.id) !== String(this.ticket.id)) {
            throw new Error()
          }
          this.$emit('updated')
          this.closeDialog()
        } catch (e) {
          this.snackbarMessage = this.getUpdateErrorMessage(e)
          this.snackbarOpen = true
        } finally {
          this.saving = false
        }
      })
    },
  },
}
</script>

<style scoped>
.dialog-container {
  padding: 24px !important;
}

.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.dialog-title {
  font-family: 'Raleway';
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin: 0 0 12px 0;
}

.dialog-subtitle {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  line-height: 1.5;
  color: #979797;
  margin: 0;
}

.close-icon {
  cursor: pointer;
  color: #333 !important;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 16px;
}
</style>
